import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import Input from '../ui/Input';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setMessage({ type: 'error', text: 'Email address is required' });
      return;
    }

    try {
      setIsLoading(true);
      setMessage(null);

      const response = await api.post('/auth/forgot-password', { email });

      if (response.data.success) {
        setIsSubmitted(true);
        setMessage({ 
          type: 'success', 
          text: 'Password reset instructions have been sent to your email address (if it exists in our system).' 
        });
        
        // In development, also show the reset token
        if (response.data.resetToken) {
          console.log('Development Reset Token:', response.data.resetToken);
          setMessage({ 
            type: 'success', 
            text: `Password reset instructions sent. Development Token: ${response.data.resetToken}` 
          });
        }
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to send reset email' });
      }
    } catch (error: any) {
      // Always show success message to prevent email enumeration
      setIsSubmitted(true);
      setMessage({ 
        type: 'success', 
        text: 'Password reset instructions have been sent to your email address (if it exists in our system).' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setMessage(null);
    setIsSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full"
      >
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Reset Password
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
            >
              ×
            </button>
          </div>

          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                Enter your email address and we'll send you instructions to reset your password.
              </p>

              {message && message.type === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
                >
                  <p className="text-red-600 dark:text-red-400 text-sm">{message.text}</p>
                </motion.div>
              )}

              <Input
                type="email"
                label="Email Address"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
              />

              <Button
                type="submit"
                className="w-full"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send Reset Instructions'}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-4">
              {message && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-lg ${
                    message.type === 'success'
                      ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                      : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                  }`}
                >
                  <div className="text-4xl mb-2">✉️</div>
                  <p className={`text-sm ${
                    message.type === 'success'
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {message.text}
                  </p>
                </motion.div>
              )}

              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <p>If you don't receive an email within a few minutes:</p>
                <ul className="text-left space-y-1">
                  <li>• Check your spam folder</li>
                  <li>• Verify the email address is correct</li>
                  <li>• Try again in a few minutes</li>
                </ul>
              </div>

              <Button onClick={handleClose} className="w-full">
                Back to Login
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordModal;
